import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
    Box, Typography, Button, Card, CardContent, Grid, Tooltip, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Fade,
} from '@mui/material';
import axiosInstance from '../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import FollowTheSignsIcon from '@mui/icons-material/FollowTheSigns';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BlockIcon from '@mui/icons-material/Block';
import SensorsIcon from '@mui/icons-material/Sensors';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../redux/propertySlice';
import EnquiryDataTable from './EnquiryDataTable';
import EnquiryTypeChart from './EnquiryTypeChart';
import CloseIcon from '@mui/icons-material/Close';

const StyledCard = styled(Card)(({ theme }) => ({
    color: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
    textAlign: 'center',
    height: '130px',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
}));

const StyledGraphCard = styled(Card)(({ theme }) => ({
    background: '#fff',
    // color: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
    textAlign: 'center',
    height: '140px',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
}));

const StyledButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(45deg, #2196f3 30%, #21cbf3 90%)',
    color: 'black',
    borderRadius: '15px',
    padding: '6px 15px',
    fontSize: '12px',
    '&:hover': {
        background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
    },
}));

const EnquiryDashboard = () => {
    // const currentYear = dayjs().year();
    // const previousYear = currentYear - 1;
    // const [dateFrom, setDateFrom] = useState(dayjs(`${previousYear}-04-01`));
    // const [dateTo, setDateTo] = useState(dayjs(`${currentYear}-04-01`).subtract(1, 'day'));
    const today = dayjs();

    let startYear, endYear;

    if (today.month() >= 3) {
        startYear = today.year();
        endYear = today.year() + 1;
    } else {
        startYear = today.year() - 1;
        endYear = today.year();
    }

    const [dateFrom, setDateFrom] = useState(dayjs(`${startYear}-04-01`));
    const [dateTo, setDateTo] = useState(dayjs(`${endYear}-03-31`));
    const [dashboardData, setDashboardData] = useState({
        totalEnquiry: 0,
        totalFollowUps: 0,
        totalSuccess: { count: 0, percentage: 0 },
        totalRejection: { count: 0, percentage: 0 },
        totalLiveEnquiry: 0,
        enquiryTypes: [],
        enquiryStatus: [],
        closePercentage: 0,
        openPercentage: 0,
    });
    const navigate = useNavigate();
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const globalProperty = useSelector(selectGlobalProperty);
    const [open, setOpen] = useState(false);

    const enquiryType = [
        { label: 'Hot', borderColor: '#42a5f5' },
        { label: 'Cold', borderColor: '#64dd17' },
        { label: 'Warm', borderColor: '#4c85b4ff' },
        { label: 'Others', borderColor: '#ab47bc' },
        { label: 'Incorrect Lead', borderColor: '#2a4adaff' },
        { label: 'Not Connected', borderColor: '#d1046aff' },
        { label: 'Low Budget - LB', borderColor: '#813a38ff' },
        { label: 'Booked', borderColor: '#66bb6a' },
        { label: 'Duplicate', borderColor: '#66aabbff' },
        { label: 'Not Interested - NI', borderColor: '#ef5350' },
    ];

    const enqStatus = [
        { label: 'Open', borderColor: '#00695c' },
        { label: 'Close', borderColor: '#f50057' },
        { label: 'Cancel', borderColor: '#c62828' },
    ];

    useEffect(() => {
        window.scrollTo(0, 0);
        document.documentElement.style.zoom = '1';

        fetchDashboardData();
        setRefreshTrigger((prev) => prev + 1);
        return () => {
            toast.dismiss();
        };
    }, [globalProperty]);

    const fetchDashboardData = async () => {
        const flags = ['TotalEnq', 'TotalFlwup', 'TotalEnqSucess', 'TotalEnqRej', 'TotalEnqType', 'TotalEnqStatus'];
        try {
            const responses = await Promise.all(
                flags.map((flag) =>
                    axiosInstance.post('BedCalendar/DashboardEnq?currentPage=1&limit=15', {
                        PropId: globalProperty || 2,
                        BookDtFrom: dateFrom.format('YYYY-MM-DD'),
                        CheckoutDt: dateTo.format('YYYY-MM-DD'),
                        Flag: flag,
                        start: 1,
                        PageSize: 10,
                        SearchText: '',
                    })
                )
            );

            const newData = {
                totalEnquiry: 0,
                totalFollowUps: 0,
                totalSuccess: { count: 0, percentage: 0 },
                totalRejection: { count: 0, percentage: 0 },
                totalLiveEnquiry: 0,
                enquiryTypes: enquiryType.map((type) => ({
                    label: type.label,
                    borderColor: type.borderColor,
                    count: 0,
                    percentage: 0,
                })),
                enquiryStatus: [],
                closePercentage: 0,
                openPercentage: 0,
            };

            responses.forEach((response, index) => {
                if (response.data.Status === 0 && response.data.Data && response.data.Data.length > 0) {
                    const data = response.data.Data;
                    switch (flags[index]) {
                        case 'TotalEnq':
                            newData.totalEnquiry = data[0].TotalCount || 0;
                            break;
                        case 'TotalFlwup':
                            newData.totalFollowUps = data[0].TotalCount || 0;
                            break;
                        case 'TotalEnqSucess':
                            newData.totalSuccess = { count: data[0].TotalCount || 0, percentage: data[0].TotalPerc || 0 };
                            break;
                        case 'TotalEnqRej':
                            newData.totalRejection = { count: data[0].TotalCount || 0, percentage: data[0].TotalPerc || 0 };
                            break;
                        case 'TotalEnqType':
                            newData.enquiryTypes = enquiryType.map((type) => {
                                const match = data.find((item) => item.Name?.trim().toLowerCase() === type.label.trim().toLowerCase());
                                return {
                                    label: type.label,
                                    borderColor: type.borderColor,
                                    count: match ? match.TotalCount || 0 : 0,
                                    percentage: match ? match.TotalPerc || 0 : 0,
                                };
                            });
                            break;
                        case 'TotalEnqStatus':
                            newData.enquiryStatus = enqStatus.map((status) => {
                                const apiStatus = data.find((item) => item.Name?.trim().toLowerCase() === status.label.trim().toLowerCase());
                                return {
                                    label: status.label,
                                    borderColor: status.borderColor,
                                    count: apiStatus ? apiStatus.TotalCount || 0 : 0,
                                    percentage: apiStatus ? apiStatus.TotalPerc || 0 : 0,
                                };
                            });
                            const openStatus = data.find((item) => item.Name?.trim().toLowerCase() === 'open');
                            const closeStatus = data.find((item) => item.Name?.trim().toLowerCase() === 'close');
                            newData.totalLiveEnquiry = openStatus ? openStatus.TotalCount || 0 : 0;
                            newData.closePercentage = closeStatus ? closeStatus.TotalPerc || 0 : 0;
                            newData.openPercentage = openStatus ? openStatus.TotalPerc || 0 : 0;
                            const cancelStatus = data.find((item) => item.Name?.trim().toLowerCase() === 'cancel');
                            newData.totalRejection = {
                                count: cancelStatus ? cancelStatus.TotalCount || 0 : 0,
                                percentage: cancelStatus ? cancelStatus.TotalPerc || 0 : 0,
                            };
                            break;
                        default:
                            break;
                    }
                }
            });
            setDashboardData(newData);
        } catch (error) {
            toast.error('Error while fetching dashboard data');
        }
    };

    const handleRefresh = () => {
        fetchDashboardData();
        setRefreshTrigger((prev) => prev + 1);
    };

    const handleReload = () => {
        fetchDashboardData();
        setRefreshTrigger((prev) => prev + 1);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <Box
            sx={{
                p: 2,
                minHeight: '80vh',
                width: '100%',
                maxWidth: '155vw',
                overflowX: 'hidden',
                boxSizing: 'border-box',
            }}
        >
            <ToastContainer />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 2,
                }}
            >
                {/* Left Side: Back button + Title */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                    <IconButton onClick={() => navigate('/masters/company')} size="small">
                        <ArrowBackIcon sx={{ color: '#635bff' }} />
                    </IconButton>

                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            userSelect: 'none',
                        }}
                    >
                        <Box component="span" sx={{ color: '#2196f3' }}>E</Box>
                        <Box component="span" sx={{ color: '#21cbf3' }}>nquiry </Box>
                        <Box component="span" sx={{ color: '#f06292' }}>D</Box>
                        <Box component="span" sx={{ color: '#ab47bc' }}>ashboard</Box>
                    </Typography>
                </Box>

                {/* Right Side: Date Pickers + Reload */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="From-Date"
                            value={dateFrom}
                            onChange={(newValue) => setDateFrom(newValue)}
                            format="DD/MM/YYYY"
                            sx={{
                                width: 150,
                                '& .MuiInputBase-input': {
                                    padding: '7px 10px !important',
                                },
                            }}
                        />
                        <DatePicker
                            label="To-Date"
                            value={dateTo}
                            onChange={(newValue) => setDateTo(newValue)}
                            format="DD/MM/YYYY"
                            sx={{
                                width: 150,
                                '& .MuiInputBase-input': {
                                    padding: '7px 10px !important',
                                },
                            }}
                        />
                    </LocalizationProvider>

                    <Tooltip title="Reload Data" arrow>
                        <StyledButton onClick={handleReload}>Reload</StyledButton>
                    </Tooltip>
                </Box>
            </Box>

            <Grid container spacing={1}>
                <Grid item xs={12} md={2.4}>
                    <Fade in={true} timeout={500}>
                        <StyledCard onDoubleClick={() => navigate('/enquiry')} sx={{ bgcolor: '#2196f3 ' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', padding: '8px', flexWrap: 'nowrap' }}>
                                <QueryStatsIcon sx={{ fontSize: 56, color: '#ffca28', mr: 3 }} />
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontSize: '25px' }}>
                                        Total Enquiry
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff' }}>
                                        {dashboardData.totalEnquiry}
                                    </Typography>
                                    <Typography sx={{ color: '#eceff1', fontSize: '16px' }}>
                                        {dashboardData.totalEnquiry > 0 ? '100%' : '0%'}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </StyledCard>
                    </Fade>
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <Fade in={true} timeout={1000}>
                        <StyledCard sx={{ bgcolor: '#009688' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', padding: '8px', flexWrap: 'nowrap' }}>
                                <SensorsIcon sx={{ fontSize: 56, color: '#817b68ff', mr: 4 }} />
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontSize: '25px' }}>
                                        Live Enquiry
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff' }}>
                                        {dashboardData.totalLiveEnquiry}
                                    </Typography>
                                    <Typography sx={{ color: '#eceff1', fontSize: '16px' }}>
                                        {dashboardData.totalEnquiry > 0
                                            ? ((dashboardData.totalLiveEnquiry / dashboardData.totalEnquiry) * 100).toFixed(2) + '%'
                                            : '0%'}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </StyledCard>
                    </Fade>
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <Fade in={true} timeout={1500}>
                        <StyledCard sx={{ bgcolor: '#8bc34a' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', padding: '8px', flexWrap: 'nowrap' }}>
                                <TrendingUpIcon sx={{ fontSize: 56, color: '#5aad23ff', mr: 4 }} />
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontSize: '25px' }}>
                                        Success %
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff' }}>
                                        {dashboardData.totalSuccess?.count ?? 0}
                                    </Typography>
                                    <Typography sx={{ color: '#eceff1', fontSize: '16px' }}>
                                        {dashboardData.totalEnquiry > 0
                                            ? ((dashboardData.totalSuccess?.count || 0) / dashboardData.totalEnquiry * 100).toFixed(2) + '%'
                                            : '0%'}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </StyledCard>
                    </Fade>
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <Fade in={true} timeout={2000}>
                        <StyledCard sx={{ bgcolor: ' #e57373' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', padding: '8px', flexWrap: 'nowrap' }}>
                                <BlockIcon sx={{ fontSize: 56, color: '#e61818ff', mr: 4 }} />
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontSize: '25px' }}>
                                        Canceled %
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff' }}>
                                        {dashboardData.totalRejection?.count ?? 0}
                                    </Typography>
                                    <Typography sx={{ fontSize: '16px' }}>
                                        {dashboardData.totalEnquiry > 0
                                            ? ((dashboardData.totalRejection?.count || 0) / dashboardData.totalEnquiry * 100).toFixed(2) + '%'
                                            : '0%'}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </StyledCard>
                    </Fade>
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <Fade in={true} timeout={2500}>
                        <StyledCard onDoubleClick={() => navigate('/TableFollowUp')} sx={{ bgcolor: '#3f51b5' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', padding: '8px', flexWrap: 'nowrap' }}>
                                <FollowTheSignsIcon sx={{ fontSize: 56, color: '#f06290', mr: 4 }} />
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontSize: '25px' }}>
                                        Follow Ups
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff' }}>
                                        {dashboardData.totalFollowUps}
                                    </Typography>
                                    <Typography sx={{ color: '#eceff1', fontSize: '16px' }}>
                                        {dashboardData.totalEnquiry > 0
                                            ? ((dashboardData.totalFollowUps / dashboardData.totalEnquiry) * 100).toFixed(2) + '%'
                                            : '0%'}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </StyledCard>
                    </Fade>
                </Grid>
            </Grid>

            <Box sx={{ mt: 2 }}>
                <Grid container spacing={0.5} justifyContent="center">
                    {dashboardData.enquiryTypes.map((item, index) => (
                        <Grid item key={`type-${index}`} xs={2.4} sm={1.2} md={1.2}>
                            <Card
                                sx={{
                                    width: { xs: '14vw', sm: 110, md: 110 },
                                    height: { xs: '14vw', sm: 110, md: 110 },
                                    maxWidth: '100%',
                                    borderRadius: '50%',
                                    backgroundColor: 'transparent',
                                    border: `4px solid ${item.borderColor}`,
                                    color: '#000',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: 1,
                                    cursor: 'pointer',
                                    transition: '0.3s',
                                    '&:hover': {
                                        boxShadow: 4,
                                    },
                                    p: 1,
                                    textAlign: 'center',
                                    flexDirection: 'column',
                                    mx: 'auto',
                                    boxSizing: 'border-box',
                                }}
                                // title={item.label}
                                onClick={handleOpen}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 'bold',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '100%',
                                        fontSize: { xs: '0.6rem', sm: '0.8rem', md: '0.8rem' },
                                    }}
                                >
                                    {item.label}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 'bold', fontSize: { xs: '0.6rem', sm: '0.8rem', md: '0.8rem' } }}
                                >
                                    {item.count}
                                </Typography>
                                <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{ fontWeight: 'bold', color: '#635bff', fontSize: { xs: '0.5rem', sm: '0.7rem', md: '0.7rem' } }}
                                >
                                    {item.percentage}%
                                </Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Box sx={{ mt: 2 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                        <StyledGraphCard sx={{ height: '260px', width: '100%', maxWidth: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    Enquiry Type Overview
                                </Typography>
                                <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                                    <EnquiryTypeChart
                                        propId={globalProperty}
                                        bookDtFrom={dateFrom.format('YYYY-MM-DD')}
                                        checkoutDt={dateTo.format('YYYY-MM-DD')}
                                        refreshTrigger={refreshTrigger}
                                    />
                                </Box>
                            </CardContent>
                        </StyledGraphCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <StyledGraphCard sx={{ height: '260px', width: '100%', maxWidth: '100%', backgroundColor: '#ccd3daff' }}>
                            <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                                <EnquiryDataTable
                                    propId={globalProperty}
                                    bookDtFrom={dateFrom.format('YYYY-MM-DD')}
                                    checkoutDt={dateTo.format('YYYY-MM-DD')}
                                    refreshTrigger={refreshTrigger}
                                />
                            </Box>
                        </StyledGraphCard>
                    </Grid>
                </Grid>
            </Box>
            <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 24px', fontWeight: 600, fontSize: '18px' }}>
                    <span>Showing Enquiry Type</span>
                    <IconButton color='error' onClick={handleClose}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent>
                    <TableContainer
                        sx={{
                            maxHeight: { xs: 320, md: 300 },
                            mt: 0.5,
                            '&::-webkit-scrollbar': {
                                width: 4,
                                height: 4
                            },
                            '&::-webkit-scrollbar-track': {
                                bgcolor: '#f5f5f5',
                                borderRadius: 2
                            },
                            '&::-webkit-scrollbar-thumb': {
                                bgcolor: '#ccc',
                                borderRadius: 2
                            }
                        }}
                    >
                        <Table stickyHeader size="small" sx={{
                            '& .MuiTableCell-root': {
                                py: 0.375,
                                px: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:first-of-type': { pl: 1.25 },
                                '&:last-child': { pr: 1.25 }
                            }
                        }}>
                            <TableHead>
                                <TableRow sx={{
                                    '& th': {
                                        bgcolor: '#d1cccc',
                                        fontWeight: 600,
                                        py: 0.75,
                                        borderBottom: '2px solid',
                                        borderColor: 'divider',
                                        whiteSpace: 'nowrap'
                                    }
                                }}>
                                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, width: '12%' }}>EnqNo</TableCell>
                                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, width: '18%' }}>Follow By</TableCell>
                                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, width: '12%' }}>EnqDate</TableCell>
                                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, width: '15%' }}>EnqType</TableCell>
                                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, width: '15%' }}>EnqBy</TableCell>
                                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, width: '15%' }}>EnqFor</TableCell>
                                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, width: '15%' }}>MobileNo</TableCell>
                                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, width: '15%' }}>Status</TableCell>
                                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, width: '15%' }}>Visit</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ padding: '2px 16px' }}>EQ00012</TableCell>
                                    <TableCell sx={{ padding: '2px 16px' }}>Bindu</TableCell>
                                    <TableCell sx={{ padding: '2px 16px' }}>10/12/2025</TableCell>
                                    <TableCell sx={{ padding: '2px 16px' }}>Hot</TableCell>
                                    <TableCell sx={{ padding: '2px 16px' }}>Aman</TableCell>
                                    <TableCell sx={{ padding: '2px 16px' }}>Hostel</TableCell>
                                    <TableCell sx={{ padding: '2px 16px' }}>8090706050</TableCell>
                                    <TableCell sx={{ padding: '2px 16px' }}>Booked</TableCell>
                                    <TableCell sx={{ padding: '2px 16px' }}>Yes</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default EnquiryDashboard;